<template>
  <div class="warehouse-dashboard">
    <div class="filter-description">
        <p>
          <i class="fa-solid fa-circle-info"></i>
          Pick a site type below, then narrow by warehouse or product category. Date range affects sales, orders, and delivery notes (DO).
          <span v-if="isSampleData" class="sample-note">Showing sample data until SAP / HIS ingest is connected.</span>
        </p>
      </div>

      <section class="site-type-section" v-if="locationGroups.length">
        <div class="section-label">
          <strong>1. Site type</strong>
          <span>Clinic = SCMS · Dental = SDMS — open those tabs or pull from Data Flow</span>
        </div>
        <div class="group-pills">
          <button
            type="button"
            class="group-pill"
            :class="{ active: filters.group === 'all' }"
            @click="selectGroup('all')"
          >
            <span class="pill-title">All sites</span>
            <span class="pill-meta">{{ formatNumber(totalSiteLines) }}</span>
          </button>
          <button
            v-for="group in locationGroups"
            :key="group.key"
            type="button"
            class="group-pill"
            :class="{ active: filters.group === group.key }"
            @click="selectGroup(group.key)"
          >
            <span class="pill-title">{{ group.label }}</span>
            <span class="pill-meta">{{ formatNumber(group.lineCount) }}</span>
          </button>
        </div>
      </section>

      <div class="filters">
        <div class="filter-panel">
          <div class="filter-block">
            <div class="section-label">
              <strong>2. Period</strong>
              <span>Sales, orders, delivery notes (DO), and GP%</span>
            </div>
            <div class="filter-row">
              <label class="date-field">
                <span>From</span>
                <input type="date" v-model="filters.from" @change="fetchDashboard">
              </label>
              <label class="date-field">
                <span>To</span>
                <input type="date" v-model="filters.to" @change="fetchDashboard">
              </label>
            </div>
          </div>

          <div class="filter-block">
            <div class="section-label">
              <strong>3. Narrow results</strong>
              <span>Optional — warehouse and product category</span>
            </div>
            <div class="filter-row">
              <label class="date-field">
                <span>Warehouse</span>
                <select v-model="filters.location" @change="fetchDashboard">
                  <option value="all">All warehouses</option>
                  <option v-for="location in filterOptions.locations" :key="location" :value="location">
                    {{ location }}
                  </option>
                </select>
              </label>
              <label class="date-field">
                <span>Product category</span>
                <select v-model="filters.category" @change="fetchDashboard">
                  <option value="all">All categories</option>
                  <option v-for="category in filterOptions.categories" :key="category" :value="category">
                    {{ category }}
                  </option>
                </select>
              </label>
              <label class="date-field">
                <span>Data source</span>
                <select v-model="filters.source" @change="fetchDashboard">
                  <option value="all">All sources</option>
                  <option v-for="source in filterOptions.sources" :key="source.code" :value="source.code">
                    {{ source.name }}
                  </option>
                </select>
              </label>
            </div>
          </div>

          <div class="filter-actions">
            <button class="export-btn refresh-btn" @click="fetchDashboard" :disabled="isLoading">
              <i class="fa-solid fa-rotate-right" :class="{ spinning: isLoading }"></i>
              Refresh
            </button>
            <button class="export-btn" @click="exportCsv" :disabled="isLoading || !inventory.length">
              <i class="fa-solid fa-file-export"></i>
              Export CSV
            </button>
          </div>
        </div>
        <div class="auto-loading-bar" v-if="isLoading">
          <div class="auto-loading-inner"></div>
        </div>
      </div>

      <div v-if="error" class="error-banner">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <div>
          <p>{{ error }}</p>
          <p class="error-sub">Please try again or contact IT if this keeps happening.</p>
        </div>
      </div>

      <section v-if="stockReminders.length" class="reminder-section" ref="reminderSection">
        <div class="reminder-banner">
          <i class="fa-solid fa-bell"></i>
          <div>
            <strong>{{ formatNumber(summary.lowStockCount) }} stock reminders</strong>
            <p>
              {{ formatNumber(summary.lowStockSkuCount) }} SKUs are at or below minimum quantity
              {{ filters.group === 'all' ? '' : ` in ${groupLabel}` }}.
            </p>
          </div>
          <button class="export-btn refresh-btn" type="button" @click="showLowStockOnly">
            Show in listing
          </button>
        </div>
        <div class="table-wrap reminder-table">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-item">Item</th>
                <th>Group</th>
                <th>Location</th>
                <th class="col-num">On-hand</th>
                <th class="col-num">Committed</th>
                <th class="col-num">Min</th>
                <th class="col-num">Short</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in stockReminders" :key="`${row.sku}-${row.location}`">
                <td class="col-item">
                  <div class="item-name">{{ row.itemName }}</div>
                  <div class="item-desc">{{ row.sku }}</div>
                </td>
                <td>{{ row.locationGroupLabel }}</td>
                <td>{{ row.location }}</td>
                <td class="col-num">{{ formatNumber(row.quantity) }}</td>
                <td class="col-num">{{ formatNumber(row.committed) }}</td>
                <td class="col-num">{{ formatNumber(row.minQuantity) }}</td>
                <td class="col-num">{{ formatNumber(row.shortage) }}</td>
                <td>{{ row.sourceName }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="!isDispensaryView" class="kpi-section fulfillment-section">
        <div class="section-heading-row">
          <div>
            <h2 class="section-title">Orders &amp; delivery of meds (DO)</h2>
            <p class="section-sub">
              Read-only from SAP sales orders and delivery notes. Pending uses current open quantity; ordered / fulfilled use the selected period.
            </p>
          </div>
          <router-link class="export-btn refresh-btn view-all-link" to="/warehouse/orders">
            View all
          </router-link>
        </div>
        <div class="summary-cards fulfillment-cards">
          <div class="card">
            <div class="card-icon"><i class="fa-solid fa-clipboard-list"></i></div>
            <div class="card-content">
              <div class="card-header">
                <h3>Ordered</h3>
                <div class="info-tooltip">
                  <i class="fa-solid fa-circle-info"></i>
                  <div class="tooltip-content">Sales order quantity in the selected period</div>
                </div>
              </div>
              <p class="amount">{{ formatNumber(summary.orderedQuantity) }}</p>
              <p class="card-meta">{{ formatNumber(summary.orderCount) }} orders</p>
            </div>
          </div>
          <div class="card">
            <div class="card-icon pending-icon"><i class="fa-solid fa-hourglass-half"></i></div>
            <div class="card-content">
              <div class="card-header">
                <h3>Pending</h3>
                <div class="info-tooltip">
                  <i class="fa-solid fa-circle-info"></i>
                  <div class="tooltip-content">Open quantity still waiting for delivery note</div>
                </div>
              </div>
              <p class="amount">{{ formatNumber(summary.pendingQuantity) }}</p>
              <p class="card-meta">{{ formatNumber(summary.pendingOrderCount) }} open orders · {{ formatNumber(summary.pendingSkuCount) }} SKUs</p>
            </div>
          </div>
          <div class="card">
            <div class="card-icon fulfilled-icon"><i class="fa-solid fa-truck-medical"></i></div>
            <div class="card-content">
              <div class="card-header">
                <h3>Fulfilled (DO)</h3>
                <div class="info-tooltip">
                  <i class="fa-solid fa-circle-info"></i>
                  <div class="tooltip-content">Delivery note quantity in the selected period</div>
                </div>
              </div>
              <p class="amount">{{ formatNumber(summary.fulfilledQuantity) }}</p>
              <p class="card-meta">{{ formatNumber(summary.deliveryCount) }} delivery notes</p>
            </div>
          </div>
        </div>

        <div class="fulfillment-tables" v-if="pendingOrders.length || recentDeliveries.length">
          <div class="panel" v-if="pendingOrders.length">
            <div class="panel-header">
              <h2>Pending order lines</h2>
              <router-link class="panel-meta link" to="/warehouse/orders?status=pending">View all pending</router-link>
            </div>
            <div class="table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th class="col-item">Item</th>
                    <th>Location</th>
                    <th class="col-num">Ordered</th>
                    <th class="col-num">Open</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in pendingOrders" :key="`po-${row.docNum}-${row.sku}-${row.location}-${row.openQuantity}`">
                    <td>{{ row.docNum }}</td>
                    <td>{{ row.docDate }}</td>
                    <td>{{ row.cardName || '—' }}</td>
                    <td class="col-item">
                      <div class="item-name">{{ row.itemName || row.sku }}</div>
                      <div class="item-desc">{{ row.sku }}</div>
                    </td>
                    <td>{{ row.location }}</td>
                    <td class="col-num">{{ formatNumber(row.quantity) }}</td>
                    <td class="col-num">{{ formatNumber(row.openQuantity) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="panel" v-if="recentDeliveries.length">
            <div class="panel-header">
              <h2>Recent delivery notes (DO)</h2>
              <router-link class="panel-meta link" to="/warehouse/orders?type=delivery">View all DOs</router-link>
            </div>
            <div class="table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>DO</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th class="col-item">Item</th>
                    <th>Location</th>
                    <th class="col-num">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in recentDeliveries" :key="`do-${row.docNum}-${row.sku}-${row.location}-${row.quantity}`">
                    <td>{{ row.docNum }}</td>
                    <td>{{ row.docDate }}</td>
                    <td>{{ row.cardName || '—' }}</td>
                    <td class="col-item">
                      <div class="item-name">{{ row.itemName || row.sku }}</div>
                      <div class="item-desc">{{ row.sku }}</div>
                    </td>
                    <td>{{ row.location }}</td>
                    <td class="col-num">{{ formatNumber(row.quantity) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section class="kpi-section">
        <h2 class="section-title">{{ isDispensaryView ? `${groupLabel} stock visibility` : 'Inventory visibility' }}</h2>
        <div class="summary-cards">
          <div class="card">
            <div class="card-icon"><i class="fa-solid fa-boxes-stacked"></i></div>
            <div class="card-content">
              <div class="card-header">
                <h3>{{ isDispensaryView ? 'Catalogue SKUs' : 'Active SKUs' }}</h3>
                <div class="info-tooltip">
                  <i class="fa-solid fa-circle-info"></i>
                  <div class="tooltip-content">
                    {{ isDispensaryView
                      ? 'Distinct medications in the selected clinic / dental sites (includes zero stock)'
                      : 'Distinct SKUs with stock in the selected location / source / category' }}
                  </div>
                </div>
              </div>
              <p class="amount">{{ formatNumber(summary.skuCount) }}</p>
            </div>
          </div>
          <div class="card">
            <div class="card-icon"><i class="fa-solid fa-warehouse"></i></div>
            <div class="card-content">
              <div class="card-header">
                <h3>On-hand quantity</h3>
              </div>
              <p class="amount">{{ formatNumber(summary.totalQuantity) }}</p>
            </div>
          </div>
          <div class="card">
            <div class="card-icon"><i class="fa-solid fa-coins"></i></div>
            <div class="card-content">
              <div class="card-header">
                <h3>Inventory value</h3>
                <div class="info-tooltip">
                  <i class="fa-solid fa-circle-info"></i>
                  <div class="tooltip-content">
                    <template v-if="isDispensaryView">
                      On-hand valued at SCMS / SDMS cost price
                      <div class="tooltip-formula">On-hand qty × cost_price</div>
                    </template>
                    <template v-else>
                      Stock on hand valued at item cost (SAP moving average)
                      <div class="tooltip-formula">On-hand qty × MovingAveragePrice</div>
                      <div class="tooltip-formula">Not invoice revenue or invoice COGS</div>
                    </template>
                  </div>
                </div>
              </div>
              <p class="amount"><CurrencyDisplay :value="summary.inventoryValue" size="large" /></p>
            </div>
          </div>
          <template v-if="isDispensaryView">
            <div class="card">
              <div class="card-icon"><i class="fa-solid fa-map-location-dot"></i></div>
              <div class="card-content">
                <div class="card-header">
                  <h3>Sites</h3>
                  <div class="info-tooltip">
                    <i class="fa-solid fa-circle-info"></i>
                    <div class="tooltip-content">Clinic or dental locations in this view</div>
                  </div>
                </div>
                <p class="amount">{{ formatNumber(summary.siteCount) }}</p>
              </div>
            </div>
            <div class="card">
              <div class="card-icon"><i class="fa-solid fa-check"></i></div>
              <div class="card-content">
                <div class="card-header">
                  <h3>With stock</h3>
                  <div class="info-tooltip">
                    <i class="fa-solid fa-circle-info"></i>
                    <div class="tooltip-content">Medication × site rows where on-hand &gt; 0</div>
                  </div>
                </div>
                <p class="amount">{{ formatNumber(summary.withStockLines) }}</p>
                <p class="card-meta">{{ formatNumber(summary.zeroStockLines) }} zero-stock rows</p>
              </div>
            </div>
            <div class="card">
              <div class="card-icon"><i class="fa-solid fa-calendar-check"></i></div>
              <div class="card-content">
                <div class="card-header">
                  <h3>Has expiry</h3>
                </div>
                <p class="amount">{{ formatNumber(summary.withExpiryLines) }}</p>
                <p class="card-meta">{{ formatNumber(summary.noExpiryLines) }} without expiry</p>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="card">
              <div class="card-icon"><i class="fa-solid fa-cart-shopping"></i></div>
              <div class="card-content">
                <div class="card-header">
                  <h3>Billed revenue</h3>
                  <div class="info-tooltip">
                    <i class="fa-solid fa-circle-info"></i>
                    <div class="tooltip-content">
                      Total from SAP A/R invoices in the date range
                      <div class="tooltip-formula">Sum of Quantity × UnitPrice</div>
                    </div>
                  </div>
                </div>
                <p class="amount"><CurrencyDisplay :value="summary.salesValue" size="large" /></p>
              </div>
            </div>
            <div class="card">
              <div class="card-icon"><i class="fa-solid fa-scale-balanced"></i></div>
              <div class="card-content">
                <div class="card-header">
                  <h3>Invoice gross profit</h3>
                  <div class="info-tooltip">
                    <i class="fa-solid fa-circle-info"></i>
                    <div class="tooltip-content">
                      SAP invoice margin in the date range
                      <div class="tooltip-formula">Billed revenue − Invoice COGS</div>
                      <div class="tooltip-formula">COGS = Quantity × GrossBuyPrice</div>
                    </div>
                  </div>
                </div>
                <p class="amount"><CurrencyDisplay :value="summary.grossProfit" size="large" /></p>
              </div>
            </div>
            <div class="card">
              <div class="card-icon"><i class="fa-solid fa-percent"></i></div>
              <div class="card-content">
                <div class="card-header">
                  <h3>Invoice GP %</h3>
                  <div class="info-tooltip">
                    <i class="fa-solid fa-circle-info"></i>
                    <div class="tooltip-content">
                      Invoice gross profit as a % of billed revenue
                      <div class="tooltip-formula">(Invoice GP ÷ Billed revenue) × 100</div>
                    </div>
                  </div>
                </div>
                <p class="amount">{{ formatPercent(summary.gpPct) }}</p>
              </div>
            </div>
          </template>
        </div>
      </section>

      <div v-if="isDispensaryView" class="dashboard-grid">
        <div class="panel">
          <div class="panel-header">
            <h2>Top stocked items</h2>
            <p class="panel-caption">Highest on-hand quantity · {{ dispensarySourceLabel }}</p>
          </div>
          <div class="panel-body chart-shell">
            <Bar v-if="topStockedChart.labels.length" :data="topStockedChart" :options="horizontalBarOptions" />
            <p v-else class="empty-text">No on-hand stock in this view.</p>
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Expiry hygiene</h2>
            <p class="panel-caption">Nearest batch expiry from {{ dispensarySourceLabel }} · months remaining</p>
          </div>
          <div class="panel-body chart-shell">
            <Bar :data="hygieneChart" :options="verticalBarOptions" />
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Stock by site</h2>
            <p class="panel-caption">On-hand quantity per {{ filters.group === 'dental' ? 'dental' : 'clinic' }}</p>
          </div>
          <div class="panel-body chart-shell">
            <Bar v-if="stockByLocationChart.labels.length" :data="stockByLocationChart" :options="horizontalBarOptions" />
            <p v-else class="empty-text">No site stock yet.</p>
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Stock coverage</h2>
            <p class="panel-caption">Catalogue rows vs on-hand · cost vs sell value</p>
          </div>
          <div class="panel-body">
            <div class="rev-cost-grid">
              <div>
                <span class="rev-label">With stock</span>
                <span class="rev-sublabel">On-hand &gt; 0</span>
                <strong>{{ formatNumber(summary.withStockLines) }}</strong>
              </div>
              <div>
                <span class="rev-label">Zero stock</span>
                <span class="rev-sublabel">Still in catalogue</span>
                <strong>{{ formatNumber(summary.zeroStockLines) }}</strong>
              </div>
              <div>
                <span class="rev-label">Stocked at cost</span>
                <span class="rev-sublabel">Qty × cost_price</span>
                <strong><CurrencyDisplay :value="summary.stockedValue" /></strong>
              </div>
              <div>
                <span class="rev-label">Stocked at sell</span>
                <span class="rev-sublabel">Qty × selling_price</span>
                <strong><CurrencyDisplay :value="summary.stockedSellValue" /></strong>
              </div>
            </div>
            <div class="chart-shell short">
              <Bar :data="stockCoverageChart" :options="verticalBarOptions" />
            </div>
          </div>
        </div>
      </div>

      <div v-else class="dashboard-grid">
        <div class="panel">
          <div class="panel-header">
            <h2>Top-selling items</h2>
          </div>
          <div class="panel-body chart-shell">
            <Bar v-if="topSellingChart.labels.length" :data="topSellingChart" :options="horizontalBarOptions" />
            <p v-else class="empty-text">No sales in the selected period.</p>
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Expiry hygiene</h2>
            <p class="panel-caption">Nearest SAP batch expiry · months remaining</p>
          </div>
          <div class="panel-body chart-shell">
            <Bar :data="hygieneChart" :options="verticalBarOptions" />
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Category performance</h2>
          </div>
          <div class="panel-body chart-shell">
            <Bar v-if="categoryChart.labels.length" :data="categoryChart" :options="horizontalBarOptions" />
            <p v-else class="empty-text">No category sales in the selected period.</p>
          </div>
        </div>

        <div class="panel">
          <div class="panel-header">
            <h2>Invoice revenue vs COGS</h2>
            <p class="panel-caption">SAP A/R invoices · UnitPrice vs GrossBuyPrice</p>
          </div>
          <div class="panel-body">
            <div class="rev-cost-grid">
              <div>
                <span class="rev-label">Billed revenue</span>
                <span class="rev-sublabel">UnitPrice × Qty</span>
                <strong><CurrencyDisplay :value="summary.salesValue" /></strong>
              </div>
              <div>
                <span class="rev-label">Invoice COGS</span>
                <span class="rev-sublabel">GrossBuyPrice × Qty</span>
                <strong><CurrencyDisplay :value="summary.salesCost" /></strong>
              </div>
              <div>
                <span class="rev-label">Invoice GP</span>
                <span class="rev-sublabel">Revenue − COGS</span>
                <strong><CurrencyDisplay :value="summary.grossProfit" /></strong>
              </div>
              <div>
                <span class="rev-label">Invoice GP %</span>
                <span class="rev-sublabel">GP ÷ revenue</span>
                <strong>{{ formatPercent(summary.gpPct) }}</strong>
              </div>
            </div>
            <div class="chart-shell short">
              <Bar :data="revenueCostChart" :options="verticalBarOptions" />
            </div>
          </div>
        </div>
      </div>

      <section class="table-section">
        <div class="table-header">
          <div>
            <h2>Inventory listing</h2>
            <p class="table-count">{{ paginationInfo }}</p>
          </div>
          <button
            v-if="hasTableFilters"
            class="clear-filters-btn"
            type="button"
            @click="clearTableFilters"
          >
            <i class="fa-solid fa-xmark"></i>
            Clear filters
          </button>
        </div>

        <div class="table-toolbar">
          <div class="search-input-group">
            <i class="fa-solid fa-search"></i>
            <input
              v-model="tableFilters.search"
              class="search-input"
              type="search"
              placeholder="Search SKU, item, description, location..."
            >
          </div>
          <select v-model="tableFilters.category">
            <option value="all">All categories</option>
            <option v-for="category in tableFilterOptions.categories" :key="category" :value="category">
              {{ category }}
            </option>
          </select>
          <select v-model="tableFilters.location">
            <option value="all">All locations</option>
            <option v-for="location in tableFilterOptions.locations" :key="location" :value="location">
              {{ location }}
            </option>
          </select>
          <select v-model="tableFilters.group">
            <option value="all">All site types</option>
            <option v-for="group in tableFilterOptions.groups" :key="group.key" :value="group.key">
              {{ group.label }}
            </option>
          </select>
          <select v-model="tableFilters.stock">
            <option value="all">All stock</option>
            <option value="low">Reorder</option>
            <option value="ok">Healthy</option>
          </select>
          <select v-model="tableFilters.expiry">
            <option value="all">All expiry</option>
            <option value="has">Has expiry</option>
            <option value="none">No expiry</option>
          </select>
          <select v-model="tableFilters.hygiene">
            <option value="all">All hygiene</option>
            <option v-for="bucket in tableFilterOptions.hygiene" :key="bucket.key" :value="bucket.key">
              {{ bucket.label }}
            </option>
          </select>
          <select v-model="tableFilters.source">
            <option value="all">All sources</option>
            <option v-for="source in tableFilterOptions.sources" :key="source" :value="source">
              {{ source }}
            </option>
          </select>
          <select v-model="tableFilters.uom">
            <option value="all">All UOM</option>
            <option v-for="uom in tableFilterOptions.uoms" :key="uom" :value="uom">
              {{ uom }}
            </option>
          </select>
        </div>

        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th class="col-no">No</th>
                <th class="col-item">Item</th>
                <th>Category</th>
                <th>UOM</th>
                <th>Location</th>
                <th class="col-num">On-hand</th>
                <th class="col-num">Committed</th>
                <th class="col-num">Available</th>
                <th class="col-num">Min</th>
                <th>Stock</th>
                <th class="col-num">Value</th>
                <th>Hygiene</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in pagedInventory" :key="`${row.sku}-${row.location}`">
                <td class="col-no">{{ rowNumber(index) }}</td>
                <td class="col-item">
                  <div class="item-name">{{ row.itemName }}</div>
                  <div class="item-desc">{{ row.sku }}</div>
                </td>
                <td>{{ row.category }}</td>
                <td>{{ row.uom }}</td>
                <td>
                  <div>{{ row.location }}</div>
                  <div class="item-desc">{{ row.warehouseCode || row.locationGroupLabel }}</div>
                </td>
                <td class="col-num">{{ formatNumber(row.quantity) }}</td>
                <td class="col-num">{{ formatNumber(row.committed) }}</td>
                <td class="col-num">{{ formatNumber(row.available) }}</td>
                <td class="col-num">{{ row.minQuantity > 0 ? formatNumber(row.minQuantity) : '—' }}</td>
                <td>
                  <span class="stock-pill" :class="row.isLowStock ? 'low' : 'ok'">
                    {{ row.isLowStock ? 'Reorder' : 'OK' }}
                  </span>
                </td>
                <td class="col-num"><CurrencyDisplay :value="row.inventoryValue" size="small" /></td>
                <td>
                  <div class="item-desc" v-if="row.expiryDate">{{ row.expiryDate }}</div>
                  <span class="hygiene-pill" :class="row.hygieneBucket">{{ row.hygieneLabel }}</span>
                </td>
              </tr>
              <tr v-if="!filteredInventory.length">
                <td colspan="12" class="empty-cell">No inventory rows match the current search or filters.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-container">
          <div class="pagination-info">
            <label class="page-size">
              Rows
              <select v-model.number="pageSize">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </label>
            <span>{{ paginationInfo }}</span>
          </div>
          <div class="pagination-controls">
            <button class="pagination-btn" :disabled="page === 1" @click="page -= 1">
              <i class="fa-solid fa-chevron-left"></i>
              Previous
            </button>
            <div class="page-numbers">
              <button
                v-for="pageNumber in visiblePages"
                :key="pageNumber"
                class="page-btn"
                :class="{ active: page === pageNumber }"
                @click="page = pageNumber"
              >
                {{ pageNumber }}
              </button>
            </div>
            <button class="pagination-btn" :disabled="page >= pageCount" @click="page += 1">
              Next
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from 'axios'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT || 15000,
  headers: { 'Content-Type': 'application/json' }
})

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

function isoDate(date) {
  return date.toISOString().slice(0, 10)
}

const start = new Date()
start.setMonth(start.getMonth() - 6)

const filters = ref({
  from: isoDate(start),
  to: isoDate(new Date()),
  group: 'all',
  location: 'all',
  source: 'all',
  category: 'all'
})

const isLoading = ref(false)
const error = ref('')
const isSampleData = ref(false)
const page = ref(1)
const pageSize = ref(10)
const reminderSection = ref(null)
const tableFilters = ref({
  search: '',
  category: 'all',
  location: 'all',
  group: 'all',
  stock: 'all',
  expiry: 'all',
  hygiene: 'all',
  source: 'all',
  uom: 'all'
})

const summary = ref({
  skuCount: 0,
  totalQuantity: 0,
  inventoryValue: 0,
  salesQuantity: 0,
  salesValue: 0,
  salesCost: 0,
  grossProfit: 0,
  gpPct: 0,
  lowStockCount: 0,
  lowStockSkuCount: 0,
  orderedQuantity: 0,
  pendingQuantity: 0,
  fulfilledQuantity: 0,
  orderCount: 0,
  openOrderCount: 0,
  closedOrderCount: 0,
  pendingOrderCount: 0,
  pendingLineCount: 0,
  pendingSkuCount: 0,
  deliveryCount: 0,
  fulfilledSkuCount: 0,
  siteCount: 0,
  withStockLines: 0,
  zeroStockLines: 0,
  withExpiryLines: 0,
  noExpiryLines: 0,
  stockedValue: 0,
  stockedSellValue: 0
})
const hygiene = ref([])
const topSelling = ref([])
const categoryPerformance = ref([])
const topStocked = ref([])
const stockByLocation = ref([])
const inventory = ref([])
const stockReminders = ref([])
const pendingOrders = ref([])
const recentDeliveries = ref([])
const locationGroups = ref([])
const filterOptions = ref({
  locations: [],
  categories: [],
  groups: [
    { key: 'clinic', label: 'Clinic' },
    { key: 'dental', label: 'Dental' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'hospital', label: 'Hospital' },
    { key: 'other', label: 'Other' }
  ],
  sources: []
})

const tableFilterOptions = computed(() => {
  const categories = new Set()
  const locations = new Set()
  const sources = new Set()
  const groups = new Map()
  const uoms = new Set()
  const hygieneMap = new Map()
  for (const row of inventory.value) {
    if (row.category) categories.add(row.category)
    if (row.location) locations.add(row.location)
    if (row.sourceName) sources.add(row.sourceName)
    if (row.uom) uoms.add(row.uom)
    if (row.locationGroup && !groups.has(row.locationGroup)) {
      groups.set(row.locationGroup, {
        key: row.locationGroup,
        label: row.locationGroupLabel || row.locationGroup
      })
    }
    if (row.hygieneBucket && !hygieneMap.has(row.hygieneBucket)) {
      hygieneMap.set(row.hygieneBucket, {
        key: row.hygieneBucket,
        label: row.hygieneLabel
      })
    }
  }
  return {
    categories: [...categories].sort(),
    locations: [...locations].sort(),
    groups: [...groups.values()],
    sources: [...sources].sort(),
    uoms: [...uoms].sort(),
    hygiene: [...hygieneMap.values()]
  }
})

const hasTableFilters = computed(() => {
  const f = tableFilters.value
  return Boolean(f.search.trim())
    || f.category !== 'all'
    || f.location !== 'all'
    || f.group !== 'all'
    || f.stock !== 'all'
    || f.expiry !== 'all'
    || f.hygiene !== 'all'
    || f.source !== 'all'
    || f.uom !== 'all'
})

const filteredInventory = computed(() => {
  const q = tableFilters.value.search.trim().toLowerCase()
  return inventory.value.filter((row) => {
    if (tableFilters.value.category !== 'all' && row.category !== tableFilters.value.category) return false
    if (tableFilters.value.location !== 'all' && row.location !== tableFilters.value.location) return false
    if (tableFilters.value.group !== 'all' && row.locationGroup !== tableFilters.value.group) return false
    if (tableFilters.value.stock === 'low' && !row.isLowStock) return false
    if (tableFilters.value.stock === 'ok' && row.isLowStock) return false
    if (tableFilters.value.expiry === 'has' && !row.expiryDate) return false
    if (tableFilters.value.expiry === 'none' && row.expiryDate) return false
    if (tableFilters.value.hygiene !== 'all' && row.hygieneBucket !== tableFilters.value.hygiene) return false
    if (tableFilters.value.source !== 'all' && row.sourceName !== tableFilters.value.source) return false
    if (tableFilters.value.uom !== 'all' && row.uom !== tableFilters.value.uom) return false
    if (!q) return true
    return [row.sku, row.itemName, row.description, row.category, row.location, row.sourceName]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q))
  })
})

const pageCount = computed(() => Math.max(1, Math.ceil(filteredInventory.value.length / pageSize.value)))
const pagedInventory = computed(() => {
  const startIndex = (page.value - 1) * pageSize.value
  return filteredInventory.value.slice(startIndex, startIndex + pageSize.value)
})

const paginationInfo = computed(() => {
  const total = filteredInventory.value.length
  if (!total) return '0 items'
  const startRow = (page.value - 1) * pageSize.value + 1
  const endRow = Math.min(page.value * pageSize.value, total)
  return `Showing ${startRow}–${endRow} of ${total} items`
})

const visiblePages = computed(() => {
  const total = pageCount.value
  const current = page.value
  const windowSize = 5
  let startPage = Math.max(1, current - Math.floor(windowSize / 2))
  let endPage = Math.min(total, startPage + windowSize - 1)
  startPage = Math.max(1, endPage - windowSize + 1)
  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)
})

function clearTableFilters() {
  tableFilters.value = {
    search: '',
    category: 'all',
    location: 'all',
    group: 'all',
    stock: 'all',
    expiry: 'all',
    hygiene: 'all',
    source: 'all',
    uom: 'all'
  }
}

watch([tableFilters, pageSize], () => {
  page.value = 1
}, { deep: true })

watch(pageCount, (count) => {
  if (page.value > count) page.value = count
})

const baseBarOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label (ctx) {
          const value = Number(ctx.raw) || 0
          return ctx.dataset.unit === 'rm'
            ? `RM ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : value.toLocaleString()
        }
      }
    }
  },
  scales: {
    x: {
      ticks: { color: '#475569', font: { size: 10 } },
      grid: { display: false }
    },
    y: {
      ticks: { color: '#64748b', font: { size: 10 } },
      grid: { color: '#e2e8f0' }
    }
  }
}

const horizontalBarOptions = computed(() => ({
  ...baseBarOptions,
  indexAxis: 'y'
}))

const verticalBarOptions = computed(() => ({
  ...baseBarOptions,
  indexAxis: 'x'
}))

const topSellingChart = computed(() => ({
  labels: topSelling.value.map((row) => row.itemName),
  datasets: [{
    label: 'Quantity sold',
    data: topSelling.value.map((row) => row.quantity),
    backgroundColor: '#0ea5e9'
  }]
}))

const hygieneChart = computed(() => ({
  labels: hygiene.value.map((row) => row.label),
  datasets: [{
    label: 'Inventory value (RM)',
    unit: 'rm',
    data: hygiene.value.map((row) => row.value),
    backgroundColor: ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444']
  }]
}))

const categoryChart = computed(() => ({
  labels: categoryPerformance.value.map((row) => row.category),
  datasets: [{
    label: 'Sales value (RM)',
    unit: 'rm',
    data: categoryPerformance.value.map((row) => row.revenue),
    backgroundColor: '#6366f1'
  }]
}))

const revenueCostChart = computed(() => ({
  labels: ['Billed revenue', 'Invoice COGS', 'Invoice GP'],
  datasets: [{
    label: 'Amount (RM)',
    unit: 'rm',
    data: [summary.value.salesValue, summary.value.salesCost, summary.value.grossProfit],
    backgroundColor: ['#0ea5e9', '#f97316', '#22c55e']
  }]
}))

const isDispensaryView = computed(() =>
  filters.value.group === 'clinic' || filters.value.group === 'dental'
)

const dispensarySourceLabel = computed(() =>
  filters.value.group === 'dental' ? 'SDMS dental' : 'SCMS clinic'
)

const topStockedChart = computed(() => ({
  labels: topStocked.value.map((row) => row.itemName),
  datasets: [{
    label: 'On-hand qty',
    data: topStocked.value.map((row) => row.quantity),
    backgroundColor: filters.value.group === 'dental' ? '#2563eb' : '#059669'
  }]
}))

const stockByLocationChart = computed(() => ({
  labels: stockByLocation.value.map((row) => row.location),
  datasets: [{
    label: 'On-hand qty',
    data: stockByLocation.value.map((row) => row.quantity),
    backgroundColor: filters.value.group === 'dental' ? '#3b82f6' : '#10b981'
  }]
}))

const stockCoverageChart = computed(() => ({
  labels: ['With stock', 'Zero stock', 'Has expiry', 'No expiry'],
  datasets: [{
    label: 'Rows',
    data: [
      summary.value.withStockLines,
      summary.value.zeroStockLines,
      summary.value.withExpiryLines,
      summary.value.noExpiryLines
    ],
    backgroundColor: ['#22c55e', '#94a3b8', '#0ea5e9', '#f59e0b']
  }]
}))

const groupLabel = computed(() => {
  if (filters.value.group === 'all') return 'All sites'
  return locationGroups.value.find((group) => group.key === filters.value.group)?.label
    || filterOptions.value.groups.find((group) => group.key === filters.value.group)?.label
    || filters.value.group
})

const totalSiteLines = computed(() =>
  locationGroups.value.reduce((sum, group) => sum + Number(group.lineCount || 0), 0)
)

function selectGroup(key) {
  filters.value.group = key
  fetchDashboard()
}

function showLowStockOnly() {
  tableFilters.value.stock = 'low'
  page.value = 1
  document.querySelector('.table-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString()
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`
}

function rowNumber(index) {
  return (page.value - 1) * pageSize.value + index + 1
}

async function fetchDashboard() {
  try {
    isLoading.value = true
    error.value = ''
    const { data } = await api.get('/api/warehouse/dashboard', { params: filters.value })
    summary.value = data.summary || summary.value
    hygiene.value = data.hygiene || []
    topSelling.value = data.topSelling || []
    categoryPerformance.value = data.categoryPerformance || []
    topStocked.value = data.topStocked || []
    stockByLocation.value = data.stockByLocation || []
    inventory.value = data.inventory || []
    stockReminders.value = data.stockReminders || []
    pendingOrders.value = data.pendingOrders || []
    recentDeliveries.value = data.recentDeliveries || []
    // Pharmacy site type hidden until pharmacy stock is wired
    locationGroups.value = (data.locationGroups || []).filter((group) => group.key !== 'pharmacy')
    const incomingFilters = data.filters || {}
    const groups = (incomingFilters.groups || filterOptions.value.groups)
      .filter((group) => group.key !== 'pharmacy')
    filterOptions.value = {
      locations: [],
      categories: [],
      sources: [],
      ...incomingFilters,
      groups
    }
    isSampleData.value = Boolean(data.isSampleData)
    page.value = 1
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load warehouse dashboard data.'
  } finally {
    isLoading.value = false
  }
}

async function exportCsv() {
  try {
    const response = await api.get('/api/warehouse/export', {
      params: filters.value,
      responseType: 'blob'
    })
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = `warehouse-inventory-${filters.value.to}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (err) {
    error.value = 'Failed to export CSV.'
  }
}

onMounted(fetchDashboard)
</script>

<style scoped>
.warehouse-dashboard {
  padding: 0;
  background: transparent;
  min-height: 0;
  width: 100%;
}

.filter-description {
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.filter-description p {
  font-size: 0.813rem;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  flex-wrap: wrap;
}

.filter-description i {
  color: #0ea5e9;
}

.sample-note {
  color: #b45309;
  font-weight: 600;
}

.filters {
  margin-bottom: 24px;
}

.filter-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.section-label {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.section-label strong {
  font-size: 0.8rem;
  color: #0f172a;
}

.section-label span {
  font-size: 0.72rem;
  color: #64748b;
}

.filter-block {
  min-width: 0;
}

.filter-row,
.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.75rem;
  color: #64748b;
}

.date-field input,
.date-field select {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 0.8rem;
  color: #0f172a;
  min-width: 150px;
}

.date-field input:focus,
.date-field select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.site-type-section {
  margin-bottom: 16px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.813rem;
  font-weight: 500;
  cursor: pointer;
}

.export-btn:hover:not(:disabled) {
  background: #0284c7;
}

.export-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn i.spinning {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.auto-loading-bar {
  width: 100%;
  height: 3px;
  margin-top: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.auto-loading-inner {
  width: 40%;
  height: 100%;
  background: #0ea5e9;
  border-radius: 999px;
  animation: loading 0.8s ease-in-out infinite alternate;
}

@keyframes loading {
  from { transform: translateX(0); }
  to { transform: translateX(150%); }
}

.error-banner {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  border-radius: 8px;
  margin-bottom: 16px;
}

.error-sub {
  font-size: 0.75rem;
  color: #7f1d1d;
  margin-top: 4px;
}

.group-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.group-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
}

.group-pill .pill-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: #0f172a;
}

.group-pill .pill-meta {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
}

.group-pill.active {
  background: #0ea5e9;
  border-color: #0ea5e9;
}

.group-pill.active .pill-title,
.group-pill.active .pill-meta {
  color: white;
}

.reminder-section {
  margin-bottom: 24px;
}

.reminder-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px 8px 0 0;
  color: #9a3412;
}

.reminder-banner i {
  font-size: 1.1rem;
  color: #ea580c;
}

.reminder-banner p {
  margin: 2px 0 0;
  font-size: 0.75rem;
  color: #c2410c;
}

.reminder-banner .export-btn {
  margin-left: auto;
  background: #ea580c;
}

.reminder-banner .export-btn:hover:not(:disabled) {
  background: #c2410c;
}

.reminder-table {
  border: 1px solid #fed7aa;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  max-height: 280px;
  overflow: auto;
}

.stock-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.stock-pill.ok {
  background: #dcfce7;
  color: #166534;
}

.stock-pill.low {
  background: #ffedd5;
  color: #9a3412;
}

.kpi-section {
  margin-bottom: 24px;
}

.section-heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading-row .section-title {
  margin-bottom: 4px;
}

.section-sub {
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
}

.view-all-link {
  text-decoration: none;
  white-space: nowrap;
}

.panel-meta.link {
  color: #0284c7;
  text-decoration: none;
}

.panel-meta.link:hover {
  text-decoration: underline;
}

.fulfillment-cards {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 16px;
}

.card-meta {
  margin: 4px 0 0;
  font-size: 0.72rem;
  color: #64748b;
}

.pending-icon {
  background: #ffedd5 !important;
  color: #c2410c !important;
}

.fulfilled-icon {
  background: #dcfce7 !important;
  color: #166534 !important;
}

.fulfillment-tables {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.fulfillment-tables .panel {
  margin: 0;
}

.fulfillment-tables .panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.fulfillment-tables .panel-header h2 {
  margin: 0;
}

.panel-meta {
  font-size: 0.72rem;
  color: #64748b;
}

.fulfillment-tables .table-wrap {
  max-height: 280px;
  overflow: auto;
}

.section-title,
.panel-header h2,
.table-header h2 {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

@media (max-width: 960px) {
  .fulfillment-cards,
  .fulfillment-tables {
    grid-template-columns: 1fr;
  }
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.card,
.panel {
  background: white;
  border-radius: 6px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card {
  padding: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0ea5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-content h3 {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.amount {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.info-tooltip {
  position: relative;
  display: inline-flex;
  cursor: help;
}

.info-tooltip i {
  font-size: 0.875rem;
  color: #94a3b8;
}

.tooltip-content {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  z-index: 20;
  margin-bottom: 8px;
}

.tooltip-formula {
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  font-family: monospace;
  color: #94a3b8;
}

.info-tooltip:hover .tooltip-content {
  opacity: 1;
  visibility: visible;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.panel-header,
.panel-body {
  padding: 16px;
}

.panel-header {
  padding-bottom: 0;
}

.chart-shell {
  height: 280px;
}

.chart-shell.short {
  height: 180px;
  margin-top: 12px;
}

.empty-text,
.empty-cell {
  color: #64748b;
  font-size: 0.875rem;
  text-align: center;
  padding: 24px 0;
}

.rev-cost-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.rev-label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
}

.rev-sublabel {
  display: block;
  font-size: 0.68rem;
  color: #94a3b8;
  margin-bottom: 2px;
}

.panel-caption {
  margin: 4px 0 0;
  font-size: 0.72rem;
  color: #64748b;
  font-weight: 400;
}

.table-section {
  background: white;
  border-radius: 6px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.table-count {
  margin: 4px 0 0;
  font-size: 0.813rem;
  color: #64748b;
}

.clear-filters-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #475569;
  border-radius: 6px;
  font-size: 0.813rem;
  cursor: pointer;
}

.clear-filters-btn:hover {
  background: #f8fafc;
  color: #1e293b;
}

.table-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.search-input-group {
  position: relative;
  flex: 1 1 240px;
  min-width: 220px;
}

.search-input-group i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.813rem;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.813rem;
  background: white;
}

.table-toolbar select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  color: #1e293b;
  font-size: 0.813rem;
  min-width: 0;
  flex: 1 1 140px;
}

.table-toolbar select:focus,
.search-input:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.12);
}

.table-wrap {
  overflow-x: hidden;
}

.data-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 8px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  color: #334155;
  vertical-align: top;
  word-break: break-word;
}

.data-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.data-table tbody tr:hover {
  background: #f8fafc;
}

.col-no {
  width: 44px;
  text-align: center;
  white-space: nowrap;
}

.col-item {
  width: 22%;
}

.col-num {
  width: 90px;
  white-space: nowrap;
}

.item-name {
  font-weight: 600;
  color: #1e293b;
}

.item-desc {
  font-size: 0.72rem;
  color: #64748b;
  margin-top: 2px;
}

.hygiene-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  background: #f1f5f9;
  color: #475569;
  white-space: nowrap;
}

.hygiene-pill.expired { background: #7f1d1d; color: #fecaca; }
.hygiene-pill.lt_3m { background: #fee2e2; color: #991b1b; }
.hygiene-pill.lt_6m { background: #ffedd5; color: #9a3412; }
.hygiene-pill.m6_12 { background: #fef9c3; color: #854d0e; }
.hygiene-pill.m12_18 { background: #ecfccb; color: #3f6212; }
.hygiene-pill.gt_18m { background: #dcfce7; color: #166534; }
.hygiene-pill.unknown { background: #f1f5f9; color: #64748b; }

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.813rem;
  color: #475569;
}

.page-size {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.page-size select {
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 0.813rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 12px;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.813rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-btn:hover:not(:disabled) {
  background: #f8fafc;
  color: #1e293b;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  min-width: 36px;
  padding: 8px 10px;
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.813rem;
  cursor: pointer;
}

.page-btn:hover {
  background: #f8fafc;
  color: #1e293b;
}

.page-btn.active {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
}

@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .summary-cards,
  .dashboard-grid,
  .rev-cost-grid {
    grid-template-columns: 1fr;
  }

  .search-input {
    min-width: 0;
    width: 100%;
  }

  .table-header {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
